import React from "react";

import { stepCompleted } from "EmployeeApplication/actions";
import { getDependents, getEmployeeApplication, getEmployeeForm, useUpdateEmployeePolicyMutation } from "EmployeeApplication/api";
import { getEnrollmentSegment } from "EmployeeApplication/selectors";
import AgeEnrollmentModal from "EmployeeCoverage/Components/EnrollmentModal/AgeEnrollmentModal";
import CoverageEnrollmentModal, { CoverageEnrollmentFormProps } from "EmployeeCoverage/Components/EnrollmentModal/CoverageEnrollmentModal";
import SegmentEnrollmentModal from "EmployeeCoverage/Components/EnrollmentModal/SegmentEnrollmentModal";
import { useDispatch, useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { formatNameFL } from "Utils/formatName";
import { EmployeeForm, EmployeePolicy, TermLifePlan, PlanCost, SegmentPlanCost, EmployeeDependent } from "Utils/types";

import Button, { ButtonProps } from "Cobalt/Button";

import { useTermLifeContext } from "./termLife.context";

interface EntitiesToAgeFormInitialValuesProps {
  employeeForm: EmployeeForm;
  employeePolicy: EmployeePolicy;
  dependents: Array<EmployeeDependent>;
}
const entitiesToAgeFormInitialValues = ({
  employeeForm,
  employeePolicy,
  dependents,
}: EntitiesToAgeFormInitialValuesProps): AgeWithCoverageEnrollmentFormProps => {
  const { dependentEnrollments } = employeePolicy;
  const dependentsFormatted = dependents.map((dependent) => {
    const dependentEnrollment = dependentEnrollments.find((dependentEnrollment) => dependent.id === dependentEnrollment.employeeDependentID);
    const enrolling = dependentEnrollment.termLife === false ? "No" : dependentEnrollment.termLife === true ? "Yes" : null;

    return {
      dependentID: dependent.id,
      formattedName: formatNameFL(dependent),
      dependentType: dependent.dependentType,
      enrolling,
    };
  });

  return {
    employeeEnrolling: employeePolicy.isEnrollingTermLife,
    employeeFormattedName: formatNameFL(employeeForm),
    dependents: dependentsFormatted,
  };
};
interface EntitiesToCoverageFormInitialValuesProps {
  employeePolicy: EmployeePolicy;
  planCost: SegmentPlanCost | null;
  selectedEmployeeAmount?: number | null;
  selectedSpouseAmount?: number | null;
  selectedChildAmount?: number | null;
}

const entitiesToCoverageFormInitialValues = ({
  employeePolicy,
  planCost,
  selectedEmployeeAmount,
  selectedSpouseAmount,
  selectedChildAmount,
}: EntitiesToCoverageFormInitialValuesProps): CoverageEnrollmentFormProps => {
  const { dependentEnrollments, employeeForm } = employeePolicy;
  const dependents = employeeForm?.employeeDependents || [];
  const spouse = dependents.find((dependent) => dependent.dependentType === "spouse" || dependent.dependentType === "domestic_partner");
  const spouseEnrollment = dependentEnrollments.find((dependentEnrollment) => spouse?.id === dependentEnrollment.employeeDependentID);
  let spouseEnrolling: string | null = null;
  if (spouseEnrollment?.termLife === false || selectedSpouseAmount === 0 || selectedSpouseAmount === null) {
    spouseEnrolling = "No";
  } else if (spouseEnrollment?.termLife === true) {
    spouseEnrolling = "Yes";
  } else {
    spouseEnrolling = null;
  }

  const child = dependents.find((dependent) => dependent.dependentType === "child");
  const childEnrollment = dependentEnrollments.find((dependentEnrollment) => child?.id === dependentEnrollment.employeeDependentID);
  let childrenEnrolling: string | null = null;
  if (childEnrollment?.termLife === false) {
    childrenEnrolling = "No";
  } else if (childEnrollment?.termLife === true) {
    childrenEnrolling = "Yes";
  } else {
    childrenEnrolling = null;
  }

  const employeeCoverageAmount = selectedEmployeeAmount;

  const spouseCoverageAmount = selectedSpouseAmount;
  const childCoverageAmount = selectedChildAmount || planCost?.childCoverageAmount;

  return {
    employeeEnrolling: employeePolicy.isEnrollingTermLife,
    spouseEnrolling,
    childrenEnrolling,
    employeeCoverageAmount,
    spouseCoverageAmount,
    childCoverageAmount,
  };
};

export const TermLifeEnrollmentModals = () => {
  const { plan, planCost, selectedEmployeeAmount, selectedSpouseAmount, selectedChildAmount, isShowing, hideEnrollmentModal } = useTermLifeContext();
  const [updateEmployeePolicy] = useUpdateEmployeePolicyMutation();
  const employeePolicy = useSelector(getEmployeeApplication);
  const employeeForm = useSelector(getEmployeeForm);
  const employeeDependents = useSelector(getDependents) || [];
  const dispatch = useDispatch();
  const closeModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "close_enroll_or_decline_modal",
    });
    hideEnrollmentModal();
  };

  const onSubmitCoverageEnrollment = async (values) => {
    const isEnrollingTermLife = values.employeeEnrolling === "Yes" ? "Yes" : "No";
    let enrollingSpouseTermLife = "No";
    let enrollingChildrenTermLife = "No";
    if (isEnrollingTermLife === "Yes") {
      enrollingSpouseTermLife = values.spouseEnrolling === "Yes" ? "Yes" : "No";
      enrollingChildrenTermLife = values.childrenEnrolling === "Yes" ? "Yes" : "No";
    }

    const spousesOrDomesticPartners = employeeDependents.filter((d) => d.dependentType === "spouse" || d.dependentType === "domestic_partner");
    const children = employeeDependents.filter((d) => d.dependentType === "child");
    const dependentsEnrollingTermLife = [];

    if (enrollingSpouseTermLife === "Yes") {
      dependentsEnrollingTermLife.push(...spousesOrDomesticPartners.map(({ id }) => id));
    }
    if (enrollingChildrenTermLife === "Yes") {
      dependentsEnrollingTermLife.push(...children.map(({ id }) => id));
    }

    const body = {
      id: employeePolicy.id,
      isEnrollingTermLife,
      enrolledTermLifePlanID: isEnrollingTermLife === "Yes" ? plan.id : null,
      dependentsEnrollingTermLife: employeeDependents.reduce((acc, { id }) => {
        acc[id] = dependentsEnrollingTermLife.includes(id);
        return acc;
      }, {}),
      termLifeCoverageAmount: isEnrollingTermLife === "Yes" ? values.employeeCoverageAmount : null,
      termLifeSpouseCoverageAmount: enrollingSpouseTermLife === "Yes" ? values.spouseCoverageAmount : null,
      termLifeChildCoverageAmount: enrollingChildrenTermLife === "Yes" ? values.childCoverageAmount : null,
    };

    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseTermLifePlan"));
    }
  };

  if (plan?.variable_coverage) {
    return (
      <CoverageEnrollmentModal
        plan={plan}
        employeePolicy={employeePolicy}
        initialValues={entitiesToCoverageFormInitialValues({
          employeePolicy,
          planCost,
          selectedEmployeeAmount,
          selectedSpouseAmount,
          selectedChildAmount,
        })}
        title="Enroll In Term Life Coverage"
        onSubmit={onSubmitCoverageEnrollment}
        onHide={closeModal}
        show={isShowing}
      />
    );
  }

  const onSubmitAgeEnrollment = async (values) => {
    const isEnrollingTermLife = values.employeeEnrolling;
    const dependentsEnrollingTermLife = {};
    values.dependents.forEach((dependent) => {
      dependentsEnrollingTermLife[dependent.dependentID] = isEnrollingTermLife === "Yes" && dependent.enrolling === "Yes";
    });
    const body = {
      id: employeePolicy.id,
      isEnrollingTermLife: values.employeeEnrolling,
      enrolledTermLifePlanID: isEnrollingTermLife === "Yes" ? plan.id : null,
      dependentsEnrollingTermLife,
      termLifeCoverageAmount: values.selectedAmount,
    };
    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseTermLifePlan"));
    }
  };

  if (planCost?.pricingType === "age") {
    return (
      <AgeEnrollmentModal
        plan={plan}
        employeePolicyID={employeePolicy.id}
        initialValues={entitiesToAgeFormInitialValues({
          employeeForm,
          employeePolicy,
          dependents: [],
        })}
        title="Enroll In Term Life Coverage"
        onSubmit={onSubmitAgeEnrollment}
        onHide={closeModal}
        show={isShowing}
      />
    );
  }

  const currentSegment = getEnrollmentSegment(employeePolicy, employeeDependents, "term_life");

  const onSubmitSegmentEnrollment = async ({ selectedSegment }) => {
    const body = {
      id: employeePolicy.id,
      isEnrollingTermLife: selectedSegment === "none" ? "No" : "Yes",
      enrolledTermLifePlanID: selectedSegment === "none" ? null : plan.id,
    };
    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseTermLifePlan"));
    }
  };

  if (planCost?.pricingType === "segment") {
    return (
      <SegmentEnrollmentModal
        plan={plan}
        planCost={planCost}
        title="Enroll In Term Life Coverage"
        initialValues={{ selectedSegment: currentSegment }}
        onSubmit={onSubmitSegmentEnrollment}
        onHide={closeModal}
        show={isShowing}
      />
    );
  }

  return null;
};

interface TermLifeEnrollOrDeclineButtonProps extends ButtonProps {
  plan: TermLifePlan;
  planCost: PlanCost;
}

export const TermLifeEnrollOrDeclineButton = ({ plan, planCost }: TermLifeEnrollOrDeclineButtonProps) => {
  const { openEnrollmentModal } = useTermLifeContext();
  const openModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "open_enroll_or_decline_modal",
    });
    openEnrollmentModal({ plan, planCost });
  };

  return <Button onClick={openModal}>Enroll or Decline</Button>;
};

export interface VariableTermLifeEnrollOrDeclineButtonProps extends TermLifeEnrollOrDeclineButtonProps {}

export const VariableTermLifeEnrollOrDeclineButton = ({
  plan,
  planCost,
  selectedEmployeeAmount,
  employeeCostAmount,
  selectedSpouseAmount,
  spouseCostAmount,
  selectedChildAmount,
  childCostAmount,
}: VariableTermLifeEnrollOrDeclineButtonProps) => {
  const { openEnrollmentModal } = useTermLifeContext();
  const openModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "open_enroll_or_decline_modal",
    });
    openEnrollmentModal({
      plan,
      planCost,
      selectedEmployeeAmount,
      employeeCostAmount,
      selectedSpouseAmount,
      spouseCostAmount,
      selectedChildAmount,
      childCostAmount,
    });
  };

  return <Button onClick={openModal}>Enroll or Decline</Button>;
};

export const TermLifeIneligibleButton = ({ plan, planCost }: TermLifeEnrollOrDeclineButtonProps) => {
  const dispatch = useDispatch();
  const employeePolicy = useSelector(getEmployeeApplication);
  const [updateEmployeePolicy] = useUpdateEmployeePolicyMutation();

  const declineCoverage = async () => {
    const body = {
      id: employeePolicy.id,
      isEnrollingTermLife: "No",
      enrolledTermLifePlanID: null,
    };
    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseTermLifePlan"));
    }
  };

  return <Button onClick={declineCoverage}>Continue</Button>;
};
